import { FC, useEffect, useRef, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { RecordingState } from './RecordingState';
import { mediaMinWidth } from '../theme/utils';
import { DuoClipboardListIcon, DuoHandDotsIcon, DuoPrescriptionBottleIcon } from '../theme/Icons';

const INTRO_PROMPTS = [
    {
        time: 0,
        text: "Mention the name of the person you're caring for, your relationship to them, and their living situation."
    },
];

const CATEGORY_PILLS = [
    { label: 'Diagnoses', Icon: DuoClipboardListIcon },
    { label: 'Medications', Icon: DuoPrescriptionBottleIcon },
    { label: 'Allergies', Icon: DuoHandDotsIcon }
];

const useStyles = createUseStyles({
    '@keyframes introPromptFadeIn': {
        from: { opacity: 0 },
        to: { opacity: 1 }
    },
    container: {
        width: '100%',
        textAlign: 'center',
        marginLeft: 'auto',
        marginRight: 'auto',
        padding: '0 16px',

        [mediaMinWidth.md]: {
            width: '65%'
        }
    },
    label: {
        fontSize: 14,
        fontWeight: 600,
        color: 'rgba(22, 166, 217, 1)',
        marginBottom: 12
    },
    prompt: {
        fontSize: 16,
        lineHeight: 1.5,
        color: 'var(--ion-color-dark)',
        fontStyle: 'italic',
        animation: '$introPromptFadeIn 0.5s ease-in'
    },
    capturesIntro: {
        marginTop: 18,
        fontSize: 14,
        lineHeight: 1.4,
        color: '#687f87',
        marginBottom: 10
    },
    pillsRow: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: 8
    },
    pill: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 13,
        fontWeight: 500,
        padding: '5px 12px',
        borderRadius: 20,
        color: 'var(--ion-color-primary)',
        backgroundColor: 'rgba(var(--ion-color-primary-rgb), 0.1)',
        border: '1px solid rgba(var(--ion-color-primary-rgb), 0.25)'
    }
});

export const IntroPrompts: FC = () => {
    const classes = useStyles();

    const recordingState = useInstance(RecordingState);
    useSubscription(() => recordingState);

    // INTRO_PROMPTS currently has one entry, so the timer below has no visible
    // effect. Kept intentionally so timed follow-up prompts can be re-added
    // without restructuring. If/when more entries return, also reset
    // elapsedSeconds when a new recording starts — otherwise a second
    // recording in the same session begins mid-sequence.
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (recordingState.isRecording() && !recordingState.isPaused()) {
            intervalRef.current = setInterval(() => {
                setElapsedSeconds(prev => prev + 1);
            }, 1000);
        } else if (recordingState.isPaused() && intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [recordingState, recordingState.state.isPaused, recordingState.state.isRecording]);

    // Find the current prompt based on elapsed time
    let currentPrompt = INTRO_PROMPTS[0];
    for (let i = INTRO_PROMPTS.length - 1; i >= 0; i--) {
        if (elapsedSeconds >= INTRO_PROMPTS[i].time) {
            currentPrompt = INTRO_PROMPTS[i];
            break;
        }
    }

    return (
        <div className={classes.container} data-id="intro-prompts">
            <div className={classes.label}>Bea is listening</div>
            <div key={currentPrompt.time} className={classes.prompt}>
                {currentPrompt.text}
            </div>
            <div className={classes.capturesIntro}>
                Bea will also capture anything you share about:
            </div>
            <div className={classes.pillsRow}>
                {CATEGORY_PILLS.map(({ label, Icon }) => (
                    <span key={label} className={classes.pill}>
                        <Icon color="primary" customSize={14} />
                        {label}
                    </span>
                ))}
            </div>
        </div>
    );
};
